import { api } from 'api';
import { DEBOUNCE_SEC } from 'constants';
import {
  BSB_NUMBER_LENGTH,
  accountNumberConfig,
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_FIELD,
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME,
  FORM_ADD_PAYTO_BSB_NAME,
  PAYTO_VALIDATE_BANK_VALIDATION_FAILURE_RESULT_CODES,
  PAYTO_VALIDATE_BANK_VALIDATION_RESULT_CODE,
  PAYTO_VALIDATE_BANK_VALIDATION_SUCCESS_RESULT_CODES,
} from 'features/to/const';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import { checkRegexValid } from 'helpers';
import {
  checkMaintenance,
  resolveSystemErrorContent,
  resolveSystemErrorTitle,
} from 'helpers/maintenance';
import useAuth from 'hooks/useAuth';
import useDebounceCallback from 'hooks/useDebounceCallback';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { SET_SHOW_LOADING } from 'store/action';
import { stringUtils } from 'utils';

const withCheckBsbAccount = (Component) => {
  const CheckBsbAccountComponent = (props) => {
    const { information, validation, setValidation, onPaytoSystemError } =
      props || {};

    const {
      dispatch,
      setPaytoCreateLimit,
      setPaytoAmendLimit,
      setPaytoBsbAccountCheckPending,
    } = usePaytoStore();
    const { token } = useAuth();

    // Ref-counted guard so this HOC only clears the shared loading flag once
    // none of ITS OWN in-flight validations remain (defect d). This does not
    // fix the pre-existing cross-HOC race with withCheckBSBNumber.jsx.
    const pendingRequestsRef = useRef(0);

    // Generation guard: bumped whenever a new account check is armed (or
    // superseded by an edit before the debounced request fires) so a stale
    // in-flight response cannot apply its result.
    const requestGenerationRef = useRef(0);

    const onCheckBsbAccountValidation = async (
      bsb,
      accountNumber,
      name,
      generation
    ) => {
      pendingRequestsRef.current += 1;
      dispatch({ type: SET_SHOW_LOADING, payload: true });
      // DEV-1401: per-call pending signal, deliberately separate from the
      // shared SET_SHOW_LOADING dispatch above (see row #5).
      setPaytoBsbAccountCheckPending(true);

      try {
        const { data } = await api.validateBsbAccount(
          token,
          bsb,
          accountNumber
        );

        if (requestGenerationRef.current !== generation) {
          return;
        }

        const { resultCode, createPayToLimit, amendPayToLimit } = data || {};

        const isSuccess =
          PAYTO_VALIDATE_BANK_VALIDATION_SUCCESS_RESULT_CODES.includes(
            resultCode
          );
        const isKnownFailure =
          PAYTO_VALIDATE_BANK_VALIDATION_FAILURE_RESULT_CODES.includes(
            resultCode
          );

        // Success (MTCH/VFYD) clears the field error. A known failure code
        // (CMTC/NMTC/NFND/CLSD) sets its mapped message. Anything
        // unrecognised is treated as a failure, not silently valid.
        const message = isSuccess
          ? ''
          : isKnownFailure
            ? PAYTO_VALIDATE_BANK_VALIDATION_RESULT_CODE?.[resultCode]
            : FORM_ADD_PAYTO_ACCOUNT_NUMBER_FIELD?.msgNumberInvalid;

        setValidation((prev) => ({
          ...prev,
          [name]: message,
        }));

        // DEV-1401: authoritative createPayToLimit/amendPayToLimit for this
        // create flow. Only applied on success, matching how the field
        // message itself is only cleared on success -- a failed/unrecognised
        // account check should not update either limit a stale or unrelated
        // prior success already established.
        //
        // Distinct from that failure case: unlike validateBSBVer2/
        // validatePayId/getPaytoAccountsAgreement, validateBsbAccount was
        // never client-confirmed to actually return either field (see the
        // ruling doc's client-confirmation list). If this response omits a
        // key entirely, treat it as "this endpoint didn't repeat the limit"
        // and preserve whatever the BSB step already established, rather
        // than blindly overwriting a real limit with undefined. A key that
        // IS present -- including an explicit null-equivalent value -- is
        // still a genuine "no limit" signal and applies as before.
        //
        // createPayToLimit and amendPayToLimit are guarded independently
        // (separate hasOwnProperty checks) rather than jointly, since either
        // key can be present without the other. This means the two values
        // in the shared store can, in principle, end up sourced from two
        // different successful responses in the same session (e.g. a later
        // check confirms createPayToLimit but omits amendPayToLimit) --
        // no case has been found where this produces a wrong guidance
        // decision, since both describe the same recipient/session and are
        // reset together, but note it if a third field is ever added here.
        if (
          isSuccess &&
          Object.prototype.hasOwnProperty.call(data || {}, 'createPayToLimit')
        ) {
          setPaytoCreateLimit(createPayToLimit);
        }
        if (
          isSuccess &&
          Object.prototype.hasOwnProperty.call(data || {}, 'amendPayToLimit')
        ) {
          setPaytoAmendLimit(amendPayToLimit);
        }
      } catch (error) {
        checkMaintenance(error);
        console.error(error?.message);

        if (requestGenerationRef.current !== generation) {
          return;
        }

        // DEV-1401: additive -- see withCheckBSBNumber.jsx for the same
        // pattern and rationale, including the 5xx-only httpErrors gate.
        // Existing inline poisoning below is unchanged, not replaced.
        const failureStatus = error?.response?.status;
        const isServerSystemFailure =
          typeof failureStatus === 'number' && failureStatus >= 500;
        const systemErrorContent = resolveSystemErrorContent(error, {
          httpErrors: isServerSystemFailure,
        });
        if (systemErrorContent != null) {
          onPaytoSystemError?.(
            resolveSystemErrorTitle(error, {
              httpErrors: isServerSystemFailure,
            }),
            systemErrorContent,
          );
        }

        setValidation((prev) => ({
          ...prev,
          [name]: FORM_ADD_PAYTO_ACCOUNT_NUMBER_FIELD?.msgNumberInvalid,
        }));
      } finally {
        pendingRequestsRef.current -= 1;
        if (pendingRequestsRef.current <= 0) {
          dispatch({ type: SET_SHOW_LOADING, payload: false });
          setPaytoBsbAccountCheckPending(false);
        }
      }
    };

    const { debounce: handleCheckBsbAccount } = useDebounceCallback(
      (bsb, accountNumber, name, generation) => {
        // DEV-1442: the useDebounceCallback hook has no cancel, so a timer
        // armed for an earlier value still fires after the account number
        // became invalid. The effect below bumps the generation in that
        // case; skip the call here, before any loading/pending write.
        if (requestGenerationRef.current !== generation) {
          return;
        }
        onCheckBsbAccountValidation(bsb, accountNumber, name, generation);
      },
      DEBOUNCE_SEC
    );

    useEffect(() => {
      const accountNumber =
        information?.[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME];
      const bsb = stringUtils.replaceAllCharacter(
        information?.[FORM_ADD_PAYTO_BSB_NAME]
      );

      // DEV-1442: an account number that is present but not a valid one must
      // not trigger account validation. Derived from the value itself (same
      // min/max/regex the input applies), not from validation[ACCOUNT_NUMBER]:
      // that key also carries server verdicts, so a stale server error there
      // could keep blocking a valid account number. Bumping the generation
      // also invalidates a debounce armed for an earlier value and any
      // in-flight response.
      const accountNumberStr = stringUtils.replaceAllCharacter(accountNumber);
      if (
        !isEmpty(accountNumber) &&
        !(
          accountNumberStr.length >= accountNumberConfig.min &&
          accountNumberStr.length <= accountNumberConfig.max &&
          checkRegexValid(accountNumberStr, accountNumberConfig.regex)
        )
      ) {
        requestGenerationRef.current += 1;
        return;
      }

      // Guard against arming (or re-arming) the debounce with a BSB that is
      // present but not a complete, valid-length BSB. Without this, editing
      // the BSB from complete to partial while a debounce from the previous
      // complete BSB is still in flight would re-run this effect and rearm
      // the debounce with the now-partial BSB, so the eventual API call
      // would validate a BSB that no longer matches what's on screen.
      //
      // Also require the BSB to be confirmed supportsPayTo (no BSB
      // validation error) before arming an account check — a well-formed
      // but unconfirmed/invalid BSB must not trigger account validation.
      if (
        isEmpty(accountNumber) ||
        isEmpty(bsb) ||
        bsb.length !== BSB_NUMBER_LENGTH ||
        validation?.[FORM_ADD_PAYTO_BSB_NAME]
      ) {
        // DEV-1442: also supersede a debounce/in-flight check armed for the
        // previous complete pair (e.g. account cleared by a BSB edit). The
        // effect re-runs and re-arms when the inputs become valid again.
        requestGenerationRef.current += 1;
        return;
      }

      requestGenerationRef.current += 1;
      const generation = requestGenerationRef.current;
      handleCheckBsbAccount(
        bsb,
        accountNumber,
        FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME,
        generation
      );
    }, [
      information?.[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME],
      information?.[FORM_ADD_PAYTO_BSB_NAME],
      validation?.[FORM_ADD_PAYTO_BSB_NAME],
    ]);

    const newProps = {
      ...props,
    };
    return <Component {...newProps} />;
  };
  return CheckBsbAccountComponent;
};

export default withCheckBsbAccount;
